<template>
  <div v-if="hasActiveFilters" class="active-filters-panel-wrapper">
    <!-- Collapsed State - Expand Button -->
    <transition name="expand-button">
      <button
        v-if="collapsed"
        class="expand-button"
        @click="togglePanel"
        @keydown.enter="togglePanel"
        @keydown.space.prevent="togglePanel"
        aria-label="Expand filters panel"
        :aria-expanded="false"
      >
        <ThemeIcon icon="chevron-right" type="icons" classes="w-5 h-5" />
      </button>
    </transition>

    <!-- Expanded State - Full Panel -->
    <transition name="panel-slide">
      <div
        v-if="!collapsed"
        class="active-filters-panel-overlay"
        @click.self="minimizePanel"
      >
        <div class="active-filters-panel-content">
          <!-- Header -->
          <div class="active-filters-panel-header">
            <div class="header-content">
              <div class="header-text">
                <h2 class="panel-title">Active Filters</h2>
                <p class="panel-subtitle">{{ activeFiltersCount }} filter{{ activeFiltersCount !== 1 ? 's' : '' }} applied</p>
              </div>
              
              <div class="header-actions">
                <button
                  class="clear-all-button"
                  @click="clearAllFilters"
                  @keydown.enter="clearAllFilters"
                  @keydown.space.prevent="clearAllFilters"
                  aria-label="Clear all filters"
                  title="Clear all filters"
                >
                  Clear All
                </button>
                
                <button
                  class="minimize-button"
                  @click="minimizePanel"
                  @keydown.enter="minimizePanel"
                  @keydown.space.prevent="minimizePanel"
                  aria-label="Minimize panel"
                  title="Minimize panel"
                  :aria-expanded="true"
                >
                  <ThemeIcon icon="chevron-left" type="icons" classes="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          
          <!-- Body -->
          <div class="active-filters-panel-body">

            <!-- ── Dataset Variables (shown when in dataset mode) ── -->
            <template v-if="isDatasetMode">
              <!-- Category label -->
              <div v-if="datasetCategoryLabel" class="dataset-category-label">
                <ThemeIcon icon="tag" type="icons" classes="w-3 h-3" />
                <span>{{ datasetCategoryLabel }}</span>
              </div>

              <!-- Variable items -->
              <div
                v-for="variable in datasetVariables"
                :key="variable.path + '_' + variable.category_id"
                class="dataset-filter-item"
              >
                <!-- Variable name -->
                <div class="dataset-filter-item__name">
                  <span :title="variable.label">{{ variable.label }}</span>
                </div>

                <!-- Scale direction toggle -->
                <button
                  class="dataset-scale-btn"
                  :class="getScaleDirection(variable.path) ? 'dataset-scale-btn--positive' : 'dataset-scale-btn--negative'"
                  :title="getScaleDirection(variable.path) ? 'Higher is Better' : 'Higher is Worse'"
                  @click="toggleScaleDirection(variable.path)"
                  :aria-label="getScaleDirection(variable.path) ? 'Higher is Better — click to toggle' : 'Higher is Worse — click to toggle'"
                >
                  <ThemeIcon icon="arrows-vertical" type="icons" classes="w-4 h-4" />
                </button>

                <!-- Weight input (multi-variable only) -->
                <div v-if="isMultiVariable" class="dataset-weight-wrapper">
                  <span class="dataset-weight-label">%</span>
                  <input
                    type="number"
                    :value="getVariableWeight(variable.path)"
                    @input="onWeightChange(variable.path, $event.target.value)"
                    min="0"
                    max="100"
                    step="1"
                    class="dataset-weight-input"
                    :aria-label="`Weight for ${variable.label}`"
                  />
                </div>

                <!-- Remove button -->
                <button
                  class="dataset-remove-btn"
                  @click="removeDatasetVariable(variable)"
                  :aria-label="`Remove ${variable.label}`"
                >
                  <ThemeIcon icon="x" type="icons" classes="w-4 h-4" />
                </button>
              </div>

              <!-- Comparison section -->
              <div class="dataset-comparison-section">
                <div class="dataset-comparison-row">
                  <span class="dataset-comparison-label">Comparison</span>
                  <label class="dataset-toggle-switch" :aria-label="'Toggle comparison mode'">
                    <input
                      type="checkbox"
                      :checked="comparisonEnabled"
                      @change="onComparisonToggle($event.target.checked)"
                    />
                    <span class="dataset-toggle-track"></span>
                  </label>
                </div>
                <transition name="expand">
                  <div v-if="comparisonEnabled" class="dataset-comparison-scope">
                    <label class="dataset-radio-label">
                      <input
                        type="radio"
                        value="state"
                        :checked="comparisonScope === 'state'"
                        name="afp-comparisonScope"
                        @change="onScopeChange('state')"
                      />
                      State Level
                    </label>
                    <label class="dataset-radio-label">
                      <input
                        type="radio"
                        value="county"
                        :checked="comparisonScope === 'county'"
                        name="afp-comparisonScope"
                        @change="onScopeChange('county')"
                      />
                      County Level
                    </label>
                  </div>
                </transition>
              </div>

              <!-- Minimum Intensity slider -->
              <div class="dataset-intensity-section">
                <div class="dataset-intensity-header">
                  <div class="dataset-intensity-title">
                    <ThemeIcon icon="lightning" type="icons" classes="w-4 h-4" />
                    <span>Minimum Intensity</span>
                  </div>
                  <span class="dataset-intensity-value">{{ opacityPercent }}%</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="100"
                  step="5"
                  :value="agsOpacityValue"
                  @input="onOpacityChange($event.target.value)"
                  class="dataset-intensity-slider"
                  aria-label="Minimum intensity"
                />
                <div class="dataset-intensity-labels">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                </div>
              </div>
            </template>

            <!-- ── Layer Filters (shown when in layer mode) ── -->
            <template v-else>
              <div
                v-for="filter in activeFiltersList"
                :key="filter.type"
                class="filter-accordion-wrapper"
              >
                <slot
                  name="filter-accordion"
                  :filter="filter"
                  :is-expanded="expandedFilter === filter.type"
                  :toggle="() => toggleAccordion(filter.type)"
                >
                  <!-- Default accordion item will be replaced by FilterAccordionItem -->
                </slot>
              </div>
            </template>

            <!-- Empty state (shouldn't show due to v-if, but as fallback) -->
            <div v-if="activeFiltersCount === 0" class="empty-state">
              <p>No active filters</p>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'ActiveFiltersPanel',
  
  components: {
    ThemeIcon
  },
  
  props: {
    activeFilters: {
      type: Object,
      required: true,
      default: () => ({})
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    // Dataset mode props
    datasetVariables: {
      type: Array,
      default: () => []
    },
    datasetCategoryLabel: {
      type: String,
      default: ''
    },
    comparisonEnabled: {
      type: Boolean,
      default: false
    },
    comparisonScope: {
      type: String,
      default: 'state'
    },
    agsOpacityValue: {
      type: Number,
      default: 70
    }
  },
  
  data() {
    return {
      expandedFilter: null,
      // Local scale directions and weights (mirrors FilterPanel logic)
      scaleDirections: {},
      variableWeights: {}
    };
  },
  
  computed: {
    isDatasetMode() {
      return this.datasetVariables.length > 0;
    },

    activeFiltersList() {
      return Object.entries(this.activeFilters)
        .filter(([type, filter]) => filter && filter.enabled)
        .map(([type, filter]) => ({
          type,
          label: filter.label || this.getFilterLabel(type),
          icon: filter.icon || this.getFilterIcon(type),
          data: filter.data || {}
        }));
    },
    
    activeFiltersCount() {
      if (this.isDatasetMode) {
        return this.datasetVariables.length;
      }
      return this.activeFiltersList.length;
    },
    
    hasActiveFilters() {
      return this.activeFiltersCount > 0;
    },

    isMultiVariable() {
      return this.datasetVariables.length >= 2;
    },

    opacityPercent() {
      return Math.round(this.agsOpacityValue);
    }
  },
  
  watch: {
    hasActiveFilters(newVal) {
      if (newVal && this.collapsed) {
        this.togglePanel();
      }
    },
    
    activeFiltersList(newList, oldList) {
      if (this.expandedFilter && !newList.find(f => f.type === this.expandedFilter)) {
        this.expandedFilter = null;
      }
      if (newList.length > oldList.length) {
        const newFilter = newList.find(f => !oldList.find(o => o.type === f.type));
        if (newFilter) {
          this.expandedFilter = newFilter.type;
        }
      }
    },

    datasetVariables(newVars, oldVars) {
      // Auto-expand panel when first variable is added
      if (newVars.length > 0 && oldVars.length === 0 && this.collapsed) {
        this.togglePanel();
      }
      // Re-initialize weights when variable count changes
      if (newVars.length !== oldVars.length) {
        this.initializeWeights();
      }
    }
  },
  
  mounted() {
    document.addEventListener('keydown', this.handleEscKey);
  },
  
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscKey);
  },
  
  methods: {
    toggleAccordion(filterType) {
      this.expandedFilter = this.expandedFilter === filterType ? null : filterType;
    },
    
    togglePanel() {
      this.$emit('toggle', !this.collapsed);
    },
    
    minimizePanel() {
      this.$emit('toggle', true);
      this.expandedFilter = null;
    },
    
    clearAllFilters() {
      this.$emit('clear-all');
      this.expandedFilter = null;
      this.scaleDirections = {};
      this.variableWeights = {};
    },
    
    handleEscKey(event) {
      if (event.key === 'Escape' && !this.collapsed) {
        this.minimizePanel();
      }
    },

    // ── Dataset-specific methods ──

    removeDatasetVariable(variable) {
      this.$emit('remove-dataset-variable', variable);
    },

    getScaleDirection(variablePath) {
      return this.scaleDirections[variablePath] ?? false;
    },

    toggleScaleDirection(variablePath) {
      this.$set(this.scaleDirections, variablePath, !this.getScaleDirection(variablePath));
      this.$emit('scale-direction-changed', {
        variablePath,
        higherIsBetter: this.scaleDirections[variablePath],
        isSingleVariable: !this.isMultiVariable
      });
    },

    getVariableWeight(variablePath) {
      if (!this.isMultiVariable) return 100;
      if (Object.keys(this.variableWeights).length === 0) {
        this.initializeWeights();
      }
      return Math.round((this.variableWeights[variablePath] || 0) * 100);
    },

    initializeWeights() {
      const count = this.datasetVariables.length;
      if (count === 0) return;
      const equalWeight = 1.0 / count;
      const newWeights = {};
      this.datasetVariables.forEach(v => { newWeights[v.path] = equalWeight; });
      this.variableWeights = newWeights;
      this.$emit('weights-changed', this.variableWeights);
    },

    onWeightChange(variablePath, value) {
      const percentage = parseFloat(value);
      if (isNaN(percentage) || percentage < 0) return;
      const newWeight = Math.min(100, Math.max(0, percentage)) / 100;
      const otherVariables = this.datasetVariables.filter(v => v.path !== variablePath);
      if (otherVariables.length === 0) {
        this.$set(this.variableWeights, variablePath, 1.0);
      } else {
        const remainingWeight = 1.0 - newWeight;
        const otherTotal = otherVariables.reduce((sum, v) => sum + (this.variableWeights[v.path] || 0), 0);
        this.$set(this.variableWeights, variablePath, newWeight);
        if (otherTotal > 0) {
          otherVariables.forEach(v => {
            const proportion = (this.variableWeights[v.path] || 0) / otherTotal;
            this.$set(this.variableWeights, v.path, remainingWeight * proportion);
          });
        } else {
          const equal = remainingWeight / otherVariables.length;
          otherVariables.forEach(v => { this.$set(this.variableWeights, v.path, equal); });
        }
      }
      this.$forceUpdate();
      this.$emit('weights-changed', this.variableWeights);
    },

    onComparisonToggle(enabled) {
      this.$emit('comparison-toggled', { enabled, scope: this.comparisonScope });
    },

    onScopeChange(value) {
      this.$emit('comparison-toggled', { enabled: this.comparisonEnabled, scope: value });
    },

    onOpacityChange(value) {
      const opacity = parseFloat(value) / 100;
      this.$emit('opacity-changed', { type: 'ags', value: opacity });
    },

    // ── Layer filter helpers ──

    getFilterLabel(type) {
      const labels = {
        travelTime: 'Travel Time',
        highway: 'Highway',
        ports: 'Ports',
        waterway: 'Waterway',
        rail: 'Rail',
        protest: 'Protest',
        directions: 'Route Planning'
      };
      return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
    },
    
    getFilterIcon(type) {
      const icons = {
        travelTime: 'clock',
        highway: 'road',
        ports: 'anchor',
        waterway: 'water',
        rail: 'train',
        protest: 'alert',
        directions: 'navigation'
      };
      return icons[type] || 'filter';
    },
    
    handleFilterUpdated(filterType, filterData) {
      this.$emit('filter-updated', { filterType, filterData });
    }
  }
};
</script>
